import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { translationPill } from './translationPill';
import { Paprika, PaprikaSpacing, PaprikaTypography } from '../../styles/theme';
import { SourceChip } from './SourceChip';
import { FactsLine } from './FactsLine';
import { TimeBoxes } from './TimeBoxes';
import { CookedLine } from './CookedLine';
import { descriptionClamp, isDescriptionCut } from './descriptionClamp';
import { htmlToPlainText } from '../../utils/stripInstructionHtml';
import { useContentWidth } from '../../hooks/useContentWidth';
import { actionsFitBesideTitle } from './headerActionsPlacement';
import { recipeTimeFacts } from '../../utils/recipeTimeFacts';
import type { RecipeTimeline, TimelineStatus } from '../../utils/recipeTimeline';

const WIDE_BREAKPOINT = 768;
/** Editorial measure — keeps the title and blurb from stretching on wide screens. */
const TEXT_MAX_WIDTH = 820;

interface RecipeHeaderProps {
  title: string;
  sourceName?: string;
  sourceUrl?: string;
  description?: string;
  sourceRating?: number;
  sourceRatingCount?: number;
  /** « Préparation »: minutes the cook's hands are busy. */
  handsOnMinutes?: number;
  /** « Prêt en »: minutes to the table, waits included. */
  readyInMinutes?: number;
  /** What the two boxes' captions are read from. */
  timeline?: RecipeTimeline;
  /** 'person': the numbers are the cook's, so the timeline's captions are left off. */
  timelineStatus?: TimelineStatus;
  servings?: number;
  isPlanned?: boolean;
  slotLabel?: string;
  timesCooked?: number;
  lastCooked?: string;
  /** Needed by CookedLine, which reads the household's verdicts directly. */
  recipeId: string;
  /** Free-form cook notes, most recent first — rendered under the cook line. */
  cookNotes?: Array<{ date: string; text: string; by?: string }>;
  isTranslated?: boolean;
  isTranslating?: boolean;
  originalLanguage?: 'en' | 'fr' | 'unknown';
  showingOriginal?: boolean;
  onToggleOriginal?: () => void;
  /** Actions rendered inline right of the title. Null on narrow layouts. */
  actions?: React.ReactNode;
}

const toSentenceCase = (str: string): string => {
  const lower = str.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * The lightened header: title, source, the two time boxes, one line of facts,
 * the cook journal when there is one, and a bare blurb.
 *
 * Gone from here: the floating white card, the category chips, the PREP/TOTAL
 * stat pills and the boxed description — all of which sat between the title and
 * the first ingredient. It ends on the page's single hairline.
 */
export const RecipeHeader: React.FC<RecipeHeaderProps> = ({
  title,
  sourceName,
  sourceUrl,
  description,
  sourceRating,
  sourceRatingCount,
  handsOnMinutes,
  readyInMinutes,
  timeline,
  timelineStatus,
  servings,
  isPlanned,
  slotLabel,
  timesCooked,
  lastCooked,
  recipeId,
  cookNotes,
  isTranslated,
  isTranslating,
  originalLanguage,
  showingOriginal,
  onToggleOriginal,
  actions,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isWideLayout = width >= WIDE_BREAKPOINT;
  // The window decides the layout, the content column decides where the actions
  // go: beside a desktop sidebar, 820px of window is 640px of column.
  const contentWidth = useContentWidth();
  const actionsBeside = actionsFitBesideTitle(contentWidth);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descFullHeight, setDescFullHeight] = useState(0);

  // Shared with the extractor: a summary escaped twice (K pour Katrine) used to
  // reach the screen as literal `<p class="Corps">` markup.
  const descriptionText = htmlToPlainText(description);
  const clamp = descriptionClamp(isWideLayout);
  const descIsCut = isDescriptionCut(descFullHeight, clamp);
  const descriptionStyle = [
    styles.description,
    { lineHeight: clamp.lineHeight },
    !isWideLayout && styles.descriptionNarrow,
  ];

  const displayTitle = title && title.trim() === title.trim().toUpperCase() && /[A-Z]/.test(title)
    ? toSentenceCase(title.trim())
    : title;

  const timeFacts = recipeTimeFacts({ handsOnMinutes, readyInMinutes, timeline, timelineStatus }, t);

  const identity = (
    <View style={isWideLayout ? styles.identityRowWide : styles.identityColumn}>
      {sourceUrl ? (
        <SourceChip
          sourceUrl={sourceUrl}
          sourceName={sourceName}
          sourceRating={sourceRating}
          sourceRatingCount={sourceRatingCount}
          flush
        />
      ) : null}
      <TimeBoxes facts={timeFacts} small={!isWideLayout} />
      <FactsLine
        hideTime
        servings={servings}
        isPlanned={isPlanned}
        slotLabel={slotLabel}
        small={!isWideLayout}
      />
    </View>
  );

  const pill = translationPill({ isTranslating, isTranslated, showingOriginal, originalLanguage });

  return (
    <View style={isWideLayout ? styles.blockWide : styles.blockNarrow}>
      <View style={styles.headerRow}>
        <View style={[styles.textColumn, isWideLayout && styles.textColumnWide]}>
          <Text style={[styles.title, !isWideLayout && styles.titleNarrow]}>{displayTitle}</Text>

          {/* Translation pill */}
          {isTranslating && (
            <Text style={styles.translatingLabel}>{t('recipe.translating')}</Text>
          )}
          {pill ? (
            <TouchableOpacity
              style={styles.translationPill}
              onPress={onToggleOriginal}
              activeOpacity={0.7}
            >
              <Ionicons name="language-outline" size={12} color={Paprika.textMuted} style={styles.translationIcon} />
              <Text style={styles.translationPillText}>
                {pill.action === 'show-translation'
                  ? t('recipe.showTranslation')
                  : pill.language.toUpperCase()}
              </Text>
            </TouchableOpacity>
          ) : null}

          {identity}

          <CookedLine
            timesCooked={timesCooked ?? 0}
            lastCooked={lastCooked}
            recipeId={recipeId}
            notes={cookNotes}
          />
        </View>

        {actions && isWideLayout && actionsBeside ? <View style={styles.actionsInline}>{actions}</View> : null}
      </View>

      {actions && isWideLayout && !actionsBeside ? (
        <View style={styles.actionsBelow} testID="recipe-actions-below">{actions}</View>
      ) : null}

      {/* Below the row, not in the title column: on an iPad the actions take
          ~400px of that row and left the blurb ~265px wide (#743). */}
      {descriptionText ? (
        <View style={styles.descriptionBlock} testID="recipe-description">
          <Text
            style={descriptionStyle}
            numberOfLines={descExpanded ? undefined : clamp.maxLines}
          >
            {descriptionText}
          </Text>
          {/* The same text with no line limit, invisible, measured to learn
              whether the clamp cut anything. RNW never fires onTextLayout. */}
          <Text
            style={[descriptionStyle, styles.descriptionMeasure]}
            onLayout={(e) => setDescFullHeight(e.nativeEvent.layout.height)}
            aria-hidden
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            pointerEvents="none"
          >
            {descriptionText}
          </Text>
          {descIsCut ? (
            <TouchableOpacity
              onPress={() => setDescExpanded(v => !v)}
              activeOpacity={0.7}
              testID="recipe-description-toggle"
            >
              <Text style={styles.descToggle}>
                {descExpanded
                  ? t('recipeDetail.showLess', 'Réduire')
                  : t('recipeDetail.showMore', 'Lire la suite')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {/* The page's single 1px rule. */}
      <View style={[styles.hairline, isWideLayout ? styles.hairlineWide : styles.hairlineNarrow]} />
    </View>
  );
};

const styles = StyleSheet.create({
  blockWide: {
    paddingTop: 26,
    paddingHorizontal: 56,
  },
  blockNarrow: {
    paddingTop: 15,
    paddingHorizontal: PaprikaSpacing.md,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 28,
  },
  textColumn: {
    flex: 1,
    minWidth: 0,
  },
  textColumnWide: {
    maxWidth: TEXT_MAX_WIDTH,
  },
  title: {
    ...PaprikaTypography.displayLarge,
    fontSize: 42,
    lineHeight: 44,
    letterSpacing: -0.63,
    color: Paprika.textPrimary,
    textAlign: 'left',
    marginBottom: 14,
  },
  titleNarrow: {
    fontSize: 29,
    lineHeight: 31,
    letterSpacing: -0.44,
    marginBottom: 12,
  },
  identityRowWide: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 18,
  },
  identityColumn: {
    alignItems: 'flex-start',
    gap: 10,
  },
  actionsInline: {
    paddingTop: 6,
    flexShrink: 0,
  },
  actionsBelow: {
    alignSelf: 'flex-start',
    marginTop: 4,
    marginBottom: 18,
  },
  translatingLabel: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginBottom: PaprikaSpacing.sm,
    fontStyle: 'italic',
  },
  translationPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 10,
    marginBottom: PaprikaSpacing.sm,
  },
  translationIcon: {
    marginRight: 4,
  },
  translationPillText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontWeight: '600',
  },
  descriptionBlock: {
    marginTop: PaprikaSpacing.md,
    maxWidth: TEXT_MAX_WIDTH,
  },
  /** lineHeight comes from `descriptionClamp`, which measures the cut with it. */
  description: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 16,
    color: Paprika.textMuted,
    textAlign: 'left',
  },
  descriptionNarrow: {
    fontSize: 14.5,
  },
  descriptionMeasure: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    opacity: 0,
  },
  descToggle: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12.5,
    color: Paprika.accent,
    marginTop: PaprikaSpacing.xs,
  },
  hairline: {
    height: 1,
    backgroundColor: Paprika.hairline,
  },
  hairlineWide: {
    marginTop: 26,
  },
  hairlineNarrow: {
    marginTop: PaprikaSpacing.md,
  },
});

export default RecipeHeader;
